<script setup lang="ts">
import type { User } from '@/types/models';
import { computed } from 'vue';
import { hasArabicRegex, hasEnglishRegex } from '@/utils/regex';
import { useI18n } from 'vue-i18n';
const { user } = defineProps<{
    user: Partial<User> & Pick<User, 'name'>;
}>();
const { t } = useI18n();
const INITIALS_BACKGROUND_COLORS_CLASSES = [
    'bg-secondary-100',
    'bg-green-100',
    'bg-blue-100',
    'bg-orange-100',
    'bg-emerald-100',
];

const initialsBackgroundColorClass = computed(() => {
    const name = user.name;
    if (!name) {
        return INITIALS_BACKGROUND_COLORS_CLASSES[0];
    }

    const nameReducedToCode = name
        .split('')
        .reduce((acc, char) => acc + char.charCodeAt(0), 0);

    const idx = nameReducedToCode % INITIALS_BACKGROUND_COLORS_CLASSES.length;
    return INITIALS_BACKGROUND_COLORS_CLASSES[idx];
});

const initials = computed(() => {
    const name = (user.name || t('generic.common_terms.unknownUser')).trim();

    // Check if name contains both Arabic and English characters
    if (hasArabicRegex.test(name) && hasEnglishRegex.test(name)) {
        return name[0].toUpperCase() + '..';
    }

    // Check if name starts with Arabic character
    if (hasArabicRegex.test(name[0])) {
        return name[0];
    }

    // Split name into words (filter out empty strings)
    const words = name.split(' ').filter(Boolean);

    if (words.length === 1) {
        return words[0][0].toUpperCase();
    }

    return words[0][0].toUpperCase() + words[words.length - 1][0].toUpperCase();
});
</script>

<template>
    <div
        class="size-10 rounded-full flex items-center justify-center text-footnote text-gray-1000 select-none"
        :class="initialsBackgroundColorClass"
    >
        {{ initials }}
    </div>
</template>
